/**
 * Shopify integration for the Fyncall Widget
 * Handles Shopify store detection, customer info, cart operations, and visitor identification
 */

import { setShopifyContext, setCartData, getCartCount, getShopifyContext as getShopifyContextFromState } from '../core/state.js';
import { checkCartValueTriggers } from '../features/proactive.js';
import { saveVisitorInfo as saveVisitorInfoToStorage, getVisitorInfo as getVisitorInfoFromStorage } from '../utils/storage.js';

/**
 * Cached Shopify context for the current page
 */
let cachedShopifyContext = null;

/**
 * Detect if we're on a Shopify store
 * @returns {boolean} Whether we're on Shopify
 */
export function isShopifyStore() {
    return !!(window.Shopify || window.ShopifyAnalytics);
}

/**
 * Detect Shopify customer and store context
 * @returns {Object|null} Shopify context
 */
export function detectShopifyContext() {
    if (!isShopifyStore()) return null;

    const context = {
        shop: null,
        customer: null,
        cart: null
    };

    try {
        // Get shop info
        if (window.Shopify?.shop) {
            context.shop = window.Shopify.shop;
        }

        // Get customer info (from Shopify meta or ShopifyAnalytics)
        if (window.ShopifyAnalytics?.meta?.page?.customerId) {
            context.customer = {
                id: window.ShopifyAnalytics.meta.page.customerId
            };
        }

        // Try to get customer from __st
        if (window.__st?.cid) {
            context.customer = context.customer || {};
            context.customer.id = window.__st.cid;
        }

        // Get customer info from meta tags
        const customerMeta = document.querySelector('meta[name="shopify-customer-id"]');
        if (customerMeta?.content) {
            context.customer = context.customer || {};
            context.customer.id = customerMeta.content;
        }

        // Try to get logged-in customer data
        if (window.theme?.customer || window.__CUSTOMER__) {
            const customerData = window.theme?.customer || window.__CUSTOMER__;
            context.customer = {
                id: customerData.id,
                email: customerData.email,
                firstName: customerData.first_name || customerData.firstName,
                lastName: customerData.last_name || customerData.lastName,
                ordersCount: customerData.orders_count,
                totalSpent: customerData.total_spent
            };
        }

        console.log('[FyncallShopify] Detected context:', context);

        // Store in state
        setShopifyContext(context);

        return context;
    } catch (e) {
        console.warn('[FyncallShopify] Error detecting context:', e);
        return null;
    }
}

/**
 * Get Shopify cart
 * @returns {Promise<Object|null>} Cart data
 */
export async function getShopifyCart() {
    if (!isShopifyStore()) return null;

    try {
        const response = await fetch('/cart.js', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) return null;

        const cart = await response.json();

        // Transform to our format
        const cartData = {
            items: cart.items.map(item => ({
                id: item.id,
                product_id: item.product_id,
                variant_id: item.variant_id,
                key: item.key,
                title: item.title,
                variant_title: item.variant_title,
                quantity: item.quantity,
                price: item.price,
                line_price: item.line_price,
                image: item.image,
                url: item.url,
                sku: item.sku
            })),
            item_count: cart.item_count,
            total_price: cart.total_price,
            currency: cart.currency || 'USD',
            requires_shipping: cart.requires_shipping
        };

        // Update state
        setCartData(cartData);

        // Check cart value triggers
        checkCartValueTriggers(cart.total_price / 100);

        return cartData;
    } catch (e) {
        console.warn('[FyncallShopify] Error getting cart:', e);
        return null;
    }
}

/**
 * Add item to Shopify cart
 * @param {number|string} variantId - Variant ID
 * @param {number} quantity - Quantity to add
 * @returns {Promise<Object|null>} Updated cart or null
 */
export async function shopifyAddToCart(variantId, quantity = 1) {
    if (!isShopifyStore()) return null;

    try {
        const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                id: variantId,
                quantity
            })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.description || 'Failed to add to cart');
        }

        // Refresh cart data
        const cart = await getShopifyCart();

        // Dispatch custom event for Shopify themes
        document.dispatchEvent(new CustomEvent('cart:change', {
            detail: { cart }
        }));

        return cart;
    } catch (e) {
        console.error('[FyncallShopify] Error adding to cart:', e);
        throw e;
    }
}

/**
 * Update cart item quantity
 * @param {string} lineKey - Line item key
 * @param {number} quantity - New quantity (0 to remove)
 * @returns {Promise<Object|null>} Updated cart or null
 */
export async function shopifyUpdateCartItem(lineKey, quantity) {
    if (!isShopifyStore()) return null;

    try {
        const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                id: lineKey,
                quantity
            })
        });

        if (!response.ok) {
            throw new Error('Failed to update cart');
        }

        // Refresh cart data
        const cart = await getShopifyCart();

        // Dispatch custom event
        document.dispatchEvent(new CustomEvent('cart:change', {
            detail: { cart }
        }));

        return cart;
    } catch (e) {
        console.error('[FyncallShopify] Error updating cart:', e);
        throw e;
    }
}

/**
 * Remove item from Shopify cart
 * @param {string} lineKey - Line item key
 * @returns {Promise<Object|null>} Updated cart or null
 */
export async function shopifyRemoveFromCart(lineKey) {
    return shopifyUpdateCartItem(lineKey, 0);
}

/**
 * Get Shopify checkout URL
 * @returns {string} Checkout URL
 */
export function getShopifyCheckoutUrl() {
    return '/checkout';
}

/**
 * Redirect to Shopify checkout
 */
export function goToCheckout() {
    window.location.href = getShopifyCheckoutUrl();
}

/**
 * Get product by handle
 * @param {string} handle - Product handle
 * @returns {Promise<Object|null>} Product data
 */
export async function getShopifyProduct(handle) {
    if (!isShopifyStore()) return null;

    try {
        const response = await fetch(`/products/${handle}.js`);
        if (!response.ok) return null;
        return response.json();
    } catch (e) {
        console.warn('[FyncallShopify] Error getting product:', e);
        return null;
    }
}

/**
 * Get collection by handle
 * @param {string} handle - Collection handle
 * @returns {Promise<Object|null>} Collection data
 */
export async function getShopifyCollection(handle) {
    if (!isShopifyStore()) return null;

    try {
        const response = await fetch(`/collections/${handle}/products.json`);
        if (!response.ok) return null;
        return response.json();
    } catch (e) {
        console.warn('[FyncallShopify] Error getting collection:', e);
        return null;
    }
}

/**
 * Get Shopify context for session creation
 * @returns {Object|null} Shopify context for API
 */
export function getShopifyContextForSession() {
    const context = detectShopifyContext();
    if (!context) return null;

    return {
        shop: context.shop,
        customer: context.customer ? {
            id: context.customer.id,
            email: context.customer.email,
            firstName: context.customer.firstName,
            lastName: context.customer.lastName,
            ordersCount: context.customer.ordersCount,
            totalSpent: context.customer.totalSpent
        } : null,
        cart: context.cart ? {
            itemCount: context.cart.item_count,
            totalPrice: context.cart.total_price
        } : null
    };
}

/**
 * Build cart context for AI requests
 * @returns {Object|null} Cart context
 */
export async function buildCartContext() {
    const cart = await getShopifyCart();
    if (!cart || !cart.items || cart.items.length === 0) {
        return null;
    }

    return {
        item_count: cart.item_count || getCartCount(),
        total_price: cart.total_price / 100,
        currency: cart.currency || 'USD',
        items: cart.items.map(item => ({
            product_id: item.product_id,
            variant_id: item.variant_id || item.id,
            key: item.key,
            title: item.title,
            variant_title: item.variant_title,
            quantity: item.quantity,
            price: item.price / 100,
            image: item.image
        }))
    };
}

/**
 * Clear all items from Shopify cart
 * @returns {Promise<Object|null>} Empty cart or null
 */
export async function clearShopifyCart() {
    if (!isShopifyStore()) return null;

    try {
        const response = await fetch('/cart/clear.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            throw new Error('Failed to clear cart');
        }

        // Refresh cart data
        const cart = await getShopifyCart();

        // Dispatch custom event
        document.dispatchEvent(new CustomEvent('cart:change', {
            detail: { cart }
        }));

        console.log('[FyncallShopify] Cart cleared');
        return cart;
    } catch (e) {
        console.error('[FyncallShopify] Error clearing cart:', e);
        throw e;
    }
}

/**
 * Handle cart action from AI response
 * @param {Object} cartAction - Action object { action, key/variant_id, quantity, product_title }
 * @param {Object} callbacks - Callbacks { onShowCheckout, onShowStatus }
 * @returns {Promise<Object|null>} Result { success, cart, message }
 */
export async function handleShopifyCartAction(cartAction, callbacks = {}) {
    if (!cartAction || !cartAction.action) return null;
    if (!isShopifyStore()) return null;

    console.log('[FyncallShopify] Handling cart action:', cartAction);

    const action = cartAction.action;
    const variantId = cartAction.key || cartAction.variant_id || cartAction.line_item_key;
    const quantity = cartAction.quantity;
    const productTitle = cartAction.product_title || 'item';

    let cart;
    let message;

    try {
        switch (action) {
            case 'update_quantity':
                cart = await shopifyUpdateCartItem(variantId, quantity);
                message = quantity === 0
                    ? `Removed "${productTitle}" from cart`
                    : `Updated "${productTitle}" quantity to ${quantity}`;
                break;

            case 'remove':
                cart = await shopifyRemoveFromCart(variantId);
                message = `Removed "${productTitle}" from cart`;
                break;

            case 'clear':
                cart = await clearShopifyCart();
                message = 'Cart cleared';
                break;

            case 'checkout':
                callbacks.onShowCheckout?.();
                return { success: true, message: null };

            default:
                console.warn('[FyncallShopify] Unknown cart action:', action);
                return null;
        }

        if (cart && message) {
            callbacks.onShowStatus?.(message, 'success');
        }

        return { success: true, cart, message };
    } catch (error) {
        console.error('[FyncallShopify] Cart action error:', error);
        callbacks.onShowStatus?.('Failed to update cart. Please try again.', 'error');
        return { success: false, error: error.message };
    }
}

/**
 * Monitor cart changes
 * @param {Function} callback - Callback when cart changes
 * @returns {Function} Cleanup function
 */
export function monitorCartChanges(callback) {
    if (!isShopifyStore()) return () => {};

    const handler = (e) => {
        if (callback) callback(e.detail?.cart);
    };

    document.addEventListener('cart:change', handler);

    // Also poll for changes (some themes don't dispatch events)
    const pollInterval = setInterval(async () => {
        const cart = await getShopifyCart();
        // Cart state is automatically updated by getShopifyCart
    }, 30000);

    return () => {
        document.removeEventListener('cart:change', handler);
        clearInterval(pollInterval);
    };
}

// =====================================================================
// VISITOR IDENTIFICATION
// =====================================================================

/**
 * Detect Shopify customer with full context including Liquid injection
 * Uses multiple methods to detect logged-in customers:
 * 1. Check injected Liquid data element (fyncall-shopify-data)
 * 2. Fallback to window.Shopify global object (via detectShopifyContext)
 * 3. Check localStorage for returning visitor info
 * @returns {Object} Full Shopify context
 */
export function detectShopifyCustomerFull() {
    // Use base detection from module
    const moduleContext = detectShopifyContext();

    // Build context object maintaining backward compatibility
    const context = {
        customer: null,
        cart: null,
        shop: null,
        detected_via: null,
        is_shopify_store: isShopifyStore(),
        returning_visitor: null
    };

    // Merge module context if available
    if (moduleContext) {
        if (moduleContext.customer) {
            context.customer = moduleContext.customer;
            context.detected_via = 'shopify_module';
        }
        if (moduleContext.shop) {
            context.shop = typeof moduleContext.shop === 'string'
                ? { domain: moduleContext.shop }
                : moduleContext.shop;
        }
        if (moduleContext.cart) {
            context.cart = moduleContext.cart;
        }
    }

    // Method 1: Check our injected Liquid data element (most reliable)
    const dataEl = document.getElementById('fyncall-shopify-data');
    if (dataEl && !context.customer) {
        try {
            const data = JSON.parse(dataEl.textContent);
            context.is_shopify_store = true;

            if (data.customer && data.customer.email) {
                context.customer = {
                    id: data.customer.id,
                    email: data.customer.email,
                    first_name: data.customer.first_name,
                    last_name: data.customer.last_name,
                    orders_count: data.customer.orders_count || 0,
                    tags: data.customer.tags || []
                };
                context.detected_via = 'liquid_injection';
                console.log('[FyncallShopify] Customer detected via Liquid:', context.customer.email);
            }

            if (data.cart && !context.cart) {
                context.cart = {
                    token: data.cart.token,
                    item_count: data.cart.item_count || 0,
                    total_price: data.cart.total_price || 0,
                    currency: data.cart.currency
                };
            }

            if (data.shop && !context.shop) {
                context.shop = {
                    domain: data.shop.domain,
                    name: data.shop.name,
                    currency: data.shop.currency
                };
            }
        } catch (e) {
            console.warn('[FyncallShopify] Failed to parse Liquid data element:', e);
        }
    }

    // Check for returning visitor in localStorage
    const savedVisitor = getVisitorInfoFromStorage();
    if (savedVisitor && savedVisitor.email) {
        context.returning_visitor = {
            name: savedVisitor.name,
            email: savedVisitor.email,
            saved_at: savedVisitor.saved_at
        };
        console.log('[FyncallShopify] Returning visitor detected:', savedVisitor.name);
    }

    // Cache the context
    cachedShopifyContext = context;

    // Store in state
    setShopifyContext(context);

    return context;
}

/**
 * Get cached Shopify context or detect if not cached
 * @returns {Object} Shopify context
 */
export function getOrDetectShopifyContext() {
    if (cachedShopifyContext) {
        return cachedShopifyContext;
    }
    return detectShopifyCustomerFull();
}

/**
 * Clear cached Shopify context (for testing or re-detection)
 */
export function clearShopifyContextCache() {
    cachedShopifyContext = null;
}

/**
 * Auto-identify customer from Shopify customer data
 * Builds visitor info and saves to localStorage for returning visits
 * @param {Object} shopifyCustomer - Shopify customer object
 * @returns {Object|null} Visitor info or null if no customer
 */
export function autoIdentifyCustomer(shopifyCustomer) {
    if (!shopifyCustomer || !shopifyCustomer.email) return null;

    const visitorInfo = {
        name: `${shopifyCustomer.first_name || ''} ${shopifyCustomer.last_name || ''}`.trim() || 'Customer',
        email: shopifyCustomer.email,
        shopify_customer_id: shopifyCustomer.id,
        orders_count: shopifyCustomer.orders_count || 0,
        tags: shopifyCustomer.tags || [],
        identified_via: 'shopify_login'
    };

    // Save to localStorage for returning visits
    saveVisitorInfoToStorage(visitorInfo);

    return visitorInfo;
}

/**
 * Extract custom fields from form data (excluding standard fields)
 * @param {Object} formData - Form data object
 * @returns {Object} Custom fields only
 */
export function extractCustomFields(formData) {
    if (!formData) return {};
    const customFields = {};
    const standardFields = ['name', 'email', 'phone', 'department'];

    Object.keys(formData).forEach(key => {
        if (!standardFields.includes(key) && formData[key] !== undefined && formData[key] !== '') {
            customFields[key] = formData[key];
        }
    });

    return customFields;
}

/**
 * Get the best available visitor info
 * Priority: Shopify customer > Pre-chat form > Returning visitor > Anonymous
 * @param {Object} options - Options
 * @param {Object} options.shopifyContext - Pre-detected Shopify context (optional)
 * @param {Object} options.prechatFormData - Pre-chat form data (optional)
 * @returns {Object} Visitor info
 */
export function getVisitorInfo({ shopifyContext = null, prechatFormData = null } = {}) {
    // Get or detect Shopify context
    const context = shopifyContext || getOrDetectShopifyContext();

    // Priority 1: Logged-in Shopify customer
    if (context?.customer?.email) {
        const shopifyInfo = autoIdentifyCustomer(context.customer);
        // Merge pre-chat form custom fields if available
        if (prechatFormData) {
            shopifyInfo.custom_fields = extractCustomFields(prechatFormData);
        }
        return shopifyInfo;
    }

    // Priority 2: Pre-chat form data (just collected)
    if (prechatFormData && (prechatFormData.name || prechatFormData.email)) {
        const visitorInfo = {
            name: prechatFormData.name || 'Website Visitor',
            email: prechatFormData.email || '',
            phone: prechatFormData.phone || '',
            identified_via: 'prechat_form',
            custom_fields: extractCustomFields(prechatFormData)
        };
        console.log('[FyncallShopify] Using pre-chat form data for visitor info');
        return visitorInfo;
    }

    // Priority 3: Returning visitor from localStorage
    const savedVisitor = getVisitorInfoFromStorage();
    if (savedVisitor && savedVisitor.email) {
        return {
            name: savedVisitor.name,
            email: savedVisitor.email,
            identified_via: 'returning_visitor'
        };
    }

    // Priority 4: Anonymous visitor
    return {
        name: 'Website Visitor',
        email: '',
        identified_via: 'anonymous'
    };
}

/**
 * Get full Shopify context for session creation
 * @param {Object} shopifyContext - Pre-detected context (optional)
 * @returns {Object|null} Shopify context for API or null if not on Shopify
 */
export function getShopifyContextForSessionFull(shopifyContext = null) {
    const context = shopifyContext || getOrDetectShopifyContext();

    // Only include if we detected this is a Shopify store
    if (!context?.is_shopify_store) {
        return null;
    }

    return {
        customer: context.customer,
        cart: context.cart,
        shop: context.shop,
        detected_via: context.detected_via
    };
}
